import { Injectable } from '@angular/core';
import { BarcodeScanner, ScanResult, SupportedFormat } from '@capacitor-community/barcode-scanner';
import { Capacitor } from '@capacitor/core';
import { AlertController, ModalController } from '@ionic/angular';
import { BarcodeScannerComponent } from './barcode-scanner.component';

@Injectable({
  providedIn: 'root',
})
export class BarcodeScannerService {
  readonly #SCANNER_MODEL_CLASS = 'scanner-modal';
  readonly #ACTIVE_SCANNER_CLASS = 'scanner-active';

  #resolveScanner!: <T>(value: TScanResult<T> | PromiseLike<TScanResult<T>>) => void;

  #ionAppNodes: Element[] = [];

  constructor(private alertController: AlertController, private modalController: ModalController) {}

  #setScannerActiveIonApp() {
    const webSplash = document.getElementById('web-splash-screen');
    if (webSplash) {
      webSplash.classList.add(this.#ACTIVE_SCANNER_CLASS);
    }
    this.#ionAppNodes = Array.from(document.getElementsByTagName('ion-app')[0].children);
    this.#ionAppNodes.forEach((elm) => {
      const isScannerModel = elm.classList.contains(this.#SCANNER_MODEL_CLASS);
      const isNotScannerModel = !isScannerModel;
      if (isNotScannerModel) {
        elm.classList.add(this.#ACTIVE_SCANNER_CLASS);
      }
    });
  }

  #showScanner() {
    // make background of WebView transparent
    // note: if you are using ionic this might not be enough, check below
    BarcodeScanner.hideBackground();
    this.#setScannerActiveIonApp();
  }

  #hideScanner() {
    const webSplash = document.getElementById('web-splash-screen');
    if (webSplash) {
      webSplash.classList.remove(this.#ACTIVE_SCANNER_CLASS);
    }
    this.#ionAppNodes.forEach((elm) => elm.classList.remove(this.#ACTIVE_SCANNER_CLASS));
  }

  #prepare() {
    BarcodeScanner.prepare();
  }

  async openBarcodeScanner<T = unknown>(hideSkip?: boolean, title?: string): Promise<TScanResult<T>> {
    const isWeb = !Capacitor.isNativePlatform();
    // Check if we
    if (isWeb) {
      return {
        type: 'WEB_UNSUPPORTED',
      };
    }

    this.#prepare();
    const modal = await this.modalController.create({
      component: BarcodeScannerComponent,
      cssClass: this.#SCANNER_MODEL_CLASS,
      showBackdrop: false,
      componentProps: {
        hideSkip: hideSkip ? hideSkip : false,
        ...(title && { title }),
      },
    });
    modal.onDidDismiss().then((overlayEvent) => {
      console.log('Dismiss Barcode', overlayEvent);
      if (!overlayEvent.data) {
        this.#stopScan('GLOBAL_BACK');
      }
    });

    await modal.present();

    this.#startScan<T>().then((v) => {
      this.#resolveScanner(v);
      this.#stopScan('CLOSE');
    });

    return new Promise<TScanResult<T>>((resolve) => {
      this.#resolveScanner = resolve as <T>(value: TScanResult<T> | PromiseLike<TScanResult<T>>) => void;
    });
  }
  async #startScan<T>(): Promise<TScanResult<T>> {
    // Check camera permission
    // This is just a simple example, check out the better checks below
    const hasPermission = await this.#didUserGrantPermission();

    if (hasPermission) {
      this.#showScanner();
      const result = await BarcodeScanner.startScan({
        targetedFormats: [SupportedFormat.QR_CODE, SupportedFormat.PDF_417],
      }); // start scanning and wait for a result

      // if the result has content
      if (result.hasContent && result.content) {
        try {
          return {
            type: 'SUCCESS_JSON',
            scanText: result.content,
            scanJson: JSON.parse(result.content),
            result,
          };
        } catch (error) {
          return {
            type: 'SUCCESS_STRING',
            scanText: result.content,
            result,
          };
        }
      }
      return {
        type: 'FAIL',
        message: 'Scan has No Content',
        result: result,
      };
    } else {
      const openSettings = await this.alertController.create({
        header: 'Camera Permission Not Give',
        message: 'Please allow Camer Permission to this app',
        buttons: [
          {
            text: 'no',
            role: 'cancel',
          },
          {
            text: 'Open Settings',
            handler: () => {
              BarcodeScanner.openAppSettings();
            },
          },
        ],
      });
      await openSettings.present();
      return {
        type: 'PERMISSION_DENIED',
      };
    }
  }

  async #stopScan(type?: 'CLOSE' | 'SKIP' | 'GLOBAL_BACK') {
    if (type === 'CLOSE' || type === 'SKIP') {
      await this.modalController.dismiss(type);
    }
    this.#hideScanner();
    BarcodeScanner.showBackground();
    await BarcodeScanner.stopScan();
    type &&
      this.#resolveScanner({
        type,
      });
  }

  scanHeaderAction(type: 'CLOSE' | 'SKIP') {
    this.#stopScan(type);
  }

  async #didUserGrantPermission(): Promise<boolean> {
    //force: false = "Just tell me the current permission status, don’t ask the user.
    //does NOT prompt user
    const status = await BarcodeScanner.checkPermission({ force: false });

    if (status.granted) return true;
    if (status.denied || status.restricted) return false;

    // If permission has never been asked, or is not granted/denied, request it
    //force: true = "Ask the user for permission if needed.
    // Prompts user if needed, otherwise returns status"

    const statusRequest = await BarcodeScanner.checkPermission({ force: true });

    // If the user just granted permission, return true
    if (statusRequest.granted) return true;

    // Otherwise, permission was not granted
    return false;
  }
}

export type TScanResult<T = unknown> =
  | {
      type: 'SUCCESS_JSON';
      scanText: string;
      scanJson: T;
      result: ScanResult;
    }
  | {
      type: 'SUCCESS_STRING';
      scanText: string;
      result: ScanResult;
    }
  | {
      type: 'PERMISSION_DENIED';
    }
  | {
      type: 'FAIL';
      message: string;
      result: ScanResult;
    }
  | {
      type: 'WEB_UNSUPPORTED';
    }
  | {
      type: 'CLOSE';
    }
  | {
      type: 'SKIP';
    }
  | {
      type: 'GLOBAL_BACK';
    };
